/**
 * React hook для управления аутентификацией
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import { LoginRequest, RegisterRequest, Trainer, TrainerUpdate } from '../types';

interface AuthState {
  isAuthenticated: boolean;
  trainer: Trainer | null;
  isLoading: boolean;
  error: string | null;
}

export const useAuth = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: apiService.isAuthenticated(),
    trainer: null,
    isLoading: false,
    error: null,
  });

  // Проверяем токен при инициализации
  useEffect(() => {
    const isAuth = apiService.isAuthenticated();
    if (authState.isAuthenticated !== isAuth) {
      setAuthState(prev => ({ ...prev, isAuthenticated: isAuth }));
      if (!isAuth) {
        // Если токен стал недействительным, очищаем профиль
        setAuthState(prev => ({ ...prev, trainer: null }));
        queryClient.removeQueries('trainer-profile');
      }
    }
  }, [authState.isAuthenticated, queryClient]);

  // Запрос профиля тренера
  const { data: trainer, isLoading: isLoadingProfile, error: profileError } = useQuery(
    'trainer-profile',
    () => apiService.getProfile(),
    {
      enabled: authState.isAuthenticated,
      retry: false,
      onSuccess: (data) => {
        setAuthState(prev => ({ ...prev, trainer: data, error: null }));
      },
      onError: (error: any) => {
        const status = error?.status_code || error?.status || error?.response?.status;
        // Если ошибка 401, пытаемся обновить токен
        if (status === 401) {
          apiService.refreshToken()
            .then(() => {
              // После успешного рефреша принудительно перезапросим профиль
              // через инвалидацию кэша
              queryClient.invalidateQueries('trainer-profile');
            })
            .catch(() => {
              setAuthState(prev => ({ ...prev, isAuthenticated: false, trainer: null, error: 'Сессия истекла' }));
              logout();
            });
        } else {
          setAuthState(prev => ({ ...prev, error: error.detail || 'Ошибка загрузки профиля' }));
        }
      }
    }
  );

  // Мутация для входа
  const loginMutation = useMutation(
    (credentials: LoginRequest) => apiService.login(credentials),
    {
      onSuccess: () => {
        setAuthState(prev => ({ ...prev, isAuthenticated: true, error: null }));
        toast.success('Вход выполнен успешно!');
        navigate('/dashboard');
        queryClient.invalidateQueries('trainer-profile');
      },
      onError: (error: any) => {
        const status = error?.status_code || error?.status || error?.response?.status;
        let errorMessage = 'Ошибка входа';
        
        // Обрабатываем разные типы ошибок
        if (status === 403) {
          errorMessage = 'Аккаунт заблокирован. Обратитесь к администратору.';
        } else if (status === 401) {
          errorMessage = 'Неверный email или пароль';
        } else {
          errorMessage = error.detail || 'Ошибка входа';
        }
        
        setAuthState(prev => ({ ...prev, error: errorMessage }));
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для регистрации
  const registerMutation = useMutation(
    (userData: RegisterRequest) => apiService.register(userData),
    {
      onSuccess: () => {
        toast.success('Регистрация успешна! Теперь войдите в систему.');
        navigate('/login');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка регистрации';
        setAuthState(prev => ({ ...prev, error: errorMessage }));
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для обновления профиля
  const updateProfileMutation = useMutation(
    (data: TrainerUpdate) => apiService.updateProfile(data),
    {
      onSuccess: (updatedTrainer) => {
        setAuthState(prev => ({ ...prev, trainer: updatedTrainer }));
        toast.success('Профиль обновлен!');
        queryClient.invalidateQueries('trainer-profile');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при обновлении профиля';
        toast.error(errorMessage);
      }
    }
  );

  // Функция выхода
  const logout = useCallback(async () => {
    try {
      await apiService.logout();
    } catch (error) {
      // Игнорируем ошибки при выходе
    } finally {
      setAuthState({
        isAuthenticated: false,
        trainer: null,
        isLoading: false,
        error: null,
      });
      queryClient.clear();
      navigate('/login');
      toast.success('Выход выполнен успешно');
    }
  }, [navigate, queryClient]);

  // Функция входа
  const login = useCallback((credentials: LoginRequest) => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    loginMutation.mutate(credentials);
  }, [loginMutation]);

  // Функция регистрации
  const register = useCallback((userData: RegisterRequest) => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    registerMutation.mutate(userData);
  }, [registerMutation]);

  // Функция обновления профиля
  const updateProfile = useCallback((data: TrainerUpdate) => {
    updateProfileMutation.mutate(data);
  }, [updateProfileMutation]);

  // Обновляем состояние загрузки
  useEffect(() => {
    const isLoading = isLoadingProfile || 
                     loginMutation.isLoading || 
                     registerMutation.isLoading || 
                     updateProfileMutation.isLoading;
    
    setAuthState(prev => ({ ...prev, isLoading }));
  }, [
    isLoadingProfile,
    loginMutation.isLoading,
    registerMutation.isLoading,
    updateProfileMutation.isLoading
  ]);

  return {
    // Состояние
    isAuthenticated: authState.isAuthenticated,
    trainer: authState.trainer,
    isLoading: authState.isLoading,
    error: authState.error,
    
    // Действия
    login,
    register,
    logout,
    updateProfile,
    
    // Состояния мутаций
    isLoginLoading: loginMutation.isLoading,
    isRegisterLoading: registerMutation.isLoading,
    isUpdateProfileLoading: updateProfileMutation.isLoading,
  };
}; 